// ============================================================
// HOOKED ENGINE — ATMOSPHERE            [v2 overhaul — new]
// The living night. One component, mounted once per screen,
// that turns a flat gradient into a place: a twinkling
// starfield, a breathing moon with halo, moonlight caustics
// rippling under the water, drifting motes, and a vignette.
//
// LAWS IN FORCE:
//   Keyframe law — every animation here is a core keyframe
//   injected at App mount (theme.ensureCoreKeyframes). No
//   <style> blocks. This file only consumes them.
//   Palette law — pure WORLD (SD.*) colors. Never chrome.
//
// Cheap by design: stars/motes are generated once (useMemo),
// all motion is CSS transform/opacity (compositor-only), and
// pointerEvents:none throughout so nothing here eats a tap.
// ============================================================

import { useMemo } from "react";
import { SD } from "./theme";

// Deterministic PRNG so the sky is stable across re-renders.
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function Atmosphere({
  stars = 90,
  motes = 18,
  moon = true,
  caustics = true,
  vignette = true,
  seed = 7,
  moonX = "76%",
  moonY = "16%",
  intensity = 1,
}) {
  const starField = useMemo(() => {
    const rnd = mulberry32(seed);
    return Array.from({ length: stars }, () => {
      const size = rnd() < 0.85 ? 1 + rnd() * 1.4 : 2 + rnd() * 1.8;
      return {
        left: rnd() * 100,
        top: rnd() * 74,                 // stars ride the sky, not the water
        size,
        s0: 0.12 + rnd() * 0.25,
        s1: 0.55 + rnd() * 0.45,
        dur: 2.4 + rnd() * 4.5,
        delay: rnd() * 6,
        warm: rnd() < 0.18,              // a few gold stars
      };
    });
  }, [stars, seed]);

  const moteField = useMemo(() => {
    const rnd = mulberry32(seed * 31 + 5);
    return Array.from({ length: motes }, () => ({
      left: rnd() * 100,
      size: 1.5 + rnd() * 3,
      dur: 14 + rnd() * 16,
      delay: -rnd() * 24,
      mx: (rnd() - 0.5) * 60,
      gold: rnd() < 0.4,
      startTop: 60 + rnd() * 45,
    }));
  }, [motes, seed]);

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      {/* deep sky wash */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(140% 100% at ${moonX} ${moonY}, ${SD.void4}55 0%, transparent 46%)`,
        opacity: 0.9 * intensity,
      }} />

      {/* stars */}
      {starField.map((s, i) => (
        <div key={i} style={{
          position: "absolute",
          left: `${s.left}%`, top: `${s.top}%`,
          width: s.size, height: s.size, borderRadius: "50%",
          background: s.warm ? SD.gold : SD.starWhite,
          boxShadow: `0 0 ${s.size * 2.5}px ${s.warm ? SD.gold : SD.starSilver}aa`,
          "--s0": s.s0, "--s1": s.s1 * intensity,
          animation: `starTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
        }} />
      ))}

      {/* moon + halo */}
      {moon && (
        <div style={{ position: "absolute", left: moonX, top: moonY, transform: "translate(-50%,-50%)" }}>
          <div style={{
            position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
            width: 220, height: 220, borderRadius: "50%",
            background: `radial-gradient(circle, ${SD.moonHalo}22 0%, ${SD.moon}12 34%, transparent 70%)`,
            animation: "moonBreathe 7s ease-in-out infinite",
          }} />
          <div style={{
            width: 62, height: 62, borderRadius: "50%",
            background: `radial-gradient(circle at 38% 34%, ${SD.moonHalo}, ${SD.moon} 55%, #b9c4a8 100%)`,
            boxShadow: `0 0 40px ${SD.moon}66, 0 0 90px ${SD.moon}33, inset -6px -6px 14px rgba(0,0,0,0.18)`,
          }} />
        </div>
      )}

      {/* moonlight caustics — refraction lattice low on the frame */}
      {caustics && (
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: 0, height: "46%",
          backgroundImage: `
            radial-gradient(circle at 20% 40%, ${SD.caustic}14 0 3px, transparent 4px),
            radial-gradient(circle at 60% 70%, ${SD.caustic}10 0 2px, transparent 3px)`,
          backgroundSize: "140px 120px, 100px 90px",
          mixBlendMode: "screen",
          maskImage: "linear-gradient(180deg, transparent, #000 55%)",
          WebkitMaskImage: "linear-gradient(180deg, transparent, #000 55%)",
          animation: "causticShift 26s linear infinite",
          opacity: 0.7 * intensity,
        }} />
      )}

      {/* rising motes */}
      {moteField.map((m, i) => (
        <div key={`m${i}`} style={{
          position: "absolute",
          left: `${m.left}%`, top: `${m.startTop}%`,
          width: m.size, height: m.size, borderRadius: "50%",
          background: m.gold ? SD.goldBright : SD.foam,
          boxShadow: `0 0 ${m.size * 2}px ${m.gold ? SD.gold : SD.caustic}88`,
          "--mx": `${m.mx}px`,
          animation: `moteDrift ${m.dur}s linear ${m.delay}s infinite`,
          opacity: 0,
        }} />
      ))}

      {/* vignette — pulls focus to center, sells depth */}
      {vignette && (
        <div style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(120% 100% at 50% 42%, transparent 52%, ${SD.void0}66 82%, ${SD.void0}cc 100%)`,
          animation: "vignettePulse 9s ease-in-out infinite",
        }} />
      )}
    </div>
  );
}
